import React, { useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { CustomTableStore, useCustomTableStore } from '../CustomTableStore'
import Box from '@mui/material/Box'
import Select from '@mui/material/Select'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from 'theme/useTheme'
import Slider from '@mui/material/Slider'
import { action } from 'mobx'
import useCustomTableRenderLog from '../hooks/useCustomTableRenderLog'
import useStore from 'reusable/mobx-store-utils'
import useElementSize from 'reusable/hooks/useElementSize'
import useClasses from 'theme/UseClasses'

type TSelectOnChange = NonNullable<React.ComponentProps<typeof Select<number>>['onChange']>

const theme = {
   spacing(val: number) {
      return val * 8
   },
}

const styles = {
   /* Styles applied to the Select component `select` class. */
   select: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(3),
      textAlign: 'right',
      textAlignLast: 'right', // Align <select> on Chrome.
   },
   /* Styles applied to the `InputBase` component. */
   input: {
      color: 'inherit',
      fontSize: 'inherit',
      flexShrink: 0,
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(4),
   },
   /* Styles applied to the caption Typography components if `variant="caption"`. */
   caption: {
      flexShrink: 0,
   },
   slider: {
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
   },
   //toolbar: { maxHeight: theme.spacing(8), minHeight: theme.spacing(7), paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1) },
   toolbar: {
      minHeight: theme.spacing(7),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
   },
   tooltipTypography: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      textAlign: 'right',
      textAlignLast: 'right',
   }
}

export const CustomTablePagination = observer(function CustomTablePagination<RowData extends AnyObject, RowStore>() {
   useCustomTableRenderLog('CustomTablePagination')
   const classes = useClasses(styles)

   const theme = useTheme()
   const customTableStore = useCustomTableStore<CustomTableStore<RowData, RowStore>>()
   const boxRef = useRef<HTMLDivElement | null>(null)
   const pagDivSize = useElementSize(boxRef.current)

   const localStore = useStore(
      sp => ({
         pageLabelOn: false,
         temporaryPage: undefined as number | undefined,
         handlePageSliderChange(event: any, page: number | number[]) {
            localStore.temporaryPage = undefined
            const newPage = (page as number) - 1
            if (newPage !== customTableStore.page) {
               customTableStore.handleChangePage(newPage)
            }
         },
         handlePageSliderTemporaryChange(event: any, page: number | number[]) {
            localStore.temporaryPage = (page as number) - 1
         },
         handlePageChange(
            event: React.ChangeEvent<{
               name?: string | undefined
               value: unknown
            }>
         ) {
            customTableStore.handleChangePage(event.target.value as number)
            localStore.setPageLabelOn()
         },
         handleRowsPerPageChange(event: Parameters<TSelectOnChange>[0]) {
            customTableStore.handleChangeRowsPerPage(event.target.value as number)
         },
         timeoutControl: undefined as NodeJS.Timeout | undefined,
         setPageLabelOn() {
            localStore.pageLabelOn = true
            if (localStore.timeoutControl !== undefined) {
               clearTimeout(localStore.timeoutControl)
            }
            localStore.timeoutControl = setTimeout(
               action(() => {
                  localStore.timeoutControl = undefined
                  localStore.pageLabelOn = false
               }),
               700
            )
         },
         handleFirstPageButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
            localStore.setPageLabelOn()
            customTableStore.handleChangePage(0)
         },
         handleBackButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
            customTableStore.handleChangePage(customTableStore.page - 1)
            localStore.setPageLabelOn()
         },
         handleNextButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
            customTableStore.handleChangePage(customTableStore.page + 1)
            localStore.setPageLabelOn()
         },
         handleLastPageButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
            customTableStore.handleChangePage(localStore.lastPage)
            localStore.setPageLabelOn()
         },
         renderSelectValue: (value: number) => (
            <Typography variant="body2">{(value as number) + ' rows / page'}</Typography>
         ),
         get paginationBoxMinHeight() {
            return customTableStore.paginationBoxMinHeight
         },
         get lastPage() {
            return Math.max(0, Math.ceil(customTableStore.totalCount / customTableStore.rowsPerPage) - 1)
         },
         get paginationControlsEnabled() {
            return localStore.pages.length > 1
         },
         get pages() {
            return generateNumbersArray(localStore.lastPage, 0)
         },
         get pagesRendered() {
            return localStore.pages.map(page => (
               <MenuItem key={page} value={page}>
                  {' '}
                  {page + 1}{' '}
               </MenuItem>
            ))
         },
         get paginationLabel() {
            const from = customTableStore.totalCount === 0 ? 0 : customTableStore.page * customTableStore.rowsPerPage + 1
            const to = Math.min(customTableStore.totalCount, (customTableStore.page + 1) * customTableStore.rowsPerPage)
            const selected = customTableStore.rowsSelectedCount
            return (
               `${from}-${to === -1 ? customTableStore.totalCount : to} of ${customTableStore.totalCount}` +
               (selected ? ` (${selected} selected)` : '')
            )
         },
         get completeSet() {
            return sp.pagDivSize.width > 400
         },
         get containerBoxStyle(): React.CSSProperties {
            return {
               minHeight: localStore.paginationBoxMinHeight,
               flex: 1,
               display: 'flex',
               flexDirection: 'column',
               position: 'relative',
            }
         },
         get containerStyle(): React.CSSProperties {
            return { bottom: '0px', right: '0px', position: 'absolute' }
         },
         get rowsDisplayedTypography(): React.CSSProperties {
            return { whiteSpace: 'nowrap' }
         },
      }),
      { pagDivSize }
   )

   return !customTableStore.sp.pagination ? null : (
      <div ref={boxRef} style={localStore.containerBoxStyle}>
         <div ref={customTableStore.paginationDivRef} style={localStore.containerStyle}>
            <Toolbar classes={{ root: classes.toolbar }}>
               <Select
                  classes={{ select: classes.select }}
                  input={<InputBase className={classes.input} />}
                  value={customTableStore.rowsPerPage}
                  onChange={localStore.handleRowsPerPageChange}
                  renderValue={localStore.renderSelectValue}>
                  {customTableStore.pageSizeOptionsRender.map(rowsPerPage => (
                     <MenuItem key={rowsPerPage} value={rowsPerPage}>
                        {' '}
                        {rowsPerPage}{' '}
                     </MenuItem>
                  ))}
               </Select>
               {localStore.completeSet && (
                  <Tooltip title="Rows being displayed">
                     <Typography
                        variant="body2"
                        classes={{ root: classes.tooltipTypography }}
                        style={localStore.rowsDisplayedTypography}>
                        {localStore.paginationLabel}
                     </Typography>
                  </Tooltip>
               )}
               <>
                  <Tooltip title="Previous Page">
                     <span>
                        <IconButton
                           onClick={localStore.handleBackButtonClick}
                           disabled={!localStore.paginationControlsEnabled || customTableStore.page === 0}
                           aria-label="previous page"
                           size="large">
                           {theme.direction === 'rtl' ? (
                              <KeyboardArrowRight />
                           ) : (
                              <KeyboardArrowLeft />
                           )}
                        </IconButton>
                     </span>
                  </Tooltip>
                  {localStore.completeSet && (
                     <Box width={80} sx={{ mt: 0.5, ml: 1, mr: 1 }}>
                        <Slider
                           disabled={!localStore.paginationControlsEnabled}
                           min={1}
                           max={localStore.lastPage + 1}
                           onChangeCommitted={localStore.handlePageSliderChange}
                           onChange={localStore.handlePageSliderTemporaryChange}
                           value={
                              localStore.temporaryPage !== undefined
                                 ? localStore.temporaryPage + 1
                                 : customTableStore.page + 1
                           }
                           valueLabelDisplay={localStore.pageLabelOn ? 'on' : 'auto'}
                           track={false}
                        />
                     </Box>
                  )}

                  <Tooltip title="Next Page">
                     <span>
                        <IconButton
                           onClick={localStore.handleNextButtonClick}
                           disabled={
                              !localStore.paginationControlsEnabled || customTableStore.page >= localStore.lastPage
                           }
                           aria-label="next page"
                           size="large">
                           {theme.direction === 'rtl' ? (
                              <KeyboardArrowLeft />
                           ) : (
                              <KeyboardArrowRight />
                           )}
                        </IconButton>
                     </span>
                  </Tooltip>
               </>
            </Toolbar>
         </div>
      </div>
   )
})

function generateNumbersArray(topNumber: number, starting = 1) {
   return Array.from(Array(topNumber - starting + 1).keys()).map(item => item + starting)
}
